import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, MailCheck, MailWarning } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { TenantShell, BrandButton, inputCls, labelCls } from "../../components/tenant/TenantShell";
import { tapi, setTenantToken, apiErr } from "../../lib/tenant";

export default function TenantVerifyEmail() {
  const { token } = useParams();
  const { sub, config, refreshMe, to } = useTenant();
  const navigate = useNavigate();
  const [state, setState] = useState("verifying");
  const [err, setErr] = useState("");
  const [email, setEmail] = useState("");
  const [resend, setResend] = useState("idle");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const { data } = await tapi.post(`/t/${sub}/auth/verify-email`, { token });
        setTenantToken(data.token);
        await refreshMe();
        setState("success");
        setTimeout(() => navigate(to("/journey")), 1200);
      } catch (e) { setErr(apiErr(e, "This verification link is invalid or has expired.")); setState("error"); }
    })();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const doResend = async (e) => {
    e.preventDefault(); setResend("sending");
    try { await tapi.post(`/t/${sub}/auth/resend-verification`, { email: email.trim() }); setResend("sent"); }
    catch (_) { setResend("idle"); }
  };

  return (
    <TenantShell>
      <div className="mx-auto max-w-md rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-sm" data-testid="tenant-verify-card">
        {state === "verifying" && (<><Loader2 className="mx-auto h-8 w-8 animate-spin text-neutral-400" /><p className="mt-4 text-sm text-neutral-600">Verifying your email…</p></>)}
        {state === "success" && (<>
          <MailCheck className="mx-auto h-10 w-10 text-emerald-600" />
          <h1 className="mt-3 text-xl font-extrabold" data-testid="tenant-verify-success">Email verified</h1>
          <p className="mt-1 text-sm text-neutral-600">Taking you to your {config.name} application…</p>
        </>)}
        {state === "error" && (<>
          <MailWarning className="mx-auto h-10 w-10 text-red-500" />
          <h1 className="mt-3 text-xl font-extrabold" data-testid="tenant-verify-error">Link not valid</h1>
          <p className="mt-1 text-sm text-neutral-600">{err}</p>
          <form onSubmit={doResend} className="mt-5 space-y-3 text-left">
            <label className="block"><span className={labelCls}>Email address</span>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@business.co.ke" data-testid="tenant-verify-resend-email" /></label>
            <BrandButton type="submit" disabled={resend !== "idle"} className="w-full" data-testid="tenant-verify-resend">
              {resend === "sending" ? <Loader2 size={14} className="animate-spin" /> : null}{resend === "sent" ? "New link sent — check your inbox" : "Send me a new link"}
            </BrandButton>
          </form>
          <p className="mt-4 text-xs text-neutral-500">Already verified? <Link to={to("/start?mode=login")} className="font-semibold underline" data-testid="tenant-verify-login">Sign in</Link></p>
        </>)}
      </div>
    </TenantShell>
  );
}
